import { Trans, useLingui } from '@lingui/react/macro'
import {
  type AssetInstanceSummary,
  fillStatusLabel,
} from 'features/fim/assetInventory'
import { FC } from 'react'
import styled from 'styled-components'

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 22rem;
  overflow-y: auto;
  padding-right: 0.2rem;
`

const Card = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;
  text-align: left;
  padding: 0.5rem 0.65rem;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.blue['400'] : theme.forecast.border};
  border-radius: 0.375rem;
  background: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background2 : theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  font: inherit;
`

const CardName = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
`

const Meta = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Chip = styled.span<{ $warn?: boolean }>`
  padding: 0.05rem 0.35rem;
  border: 1px solid
    ${({ theme, $warn }) => ($warn ? '#f97316' : theme.forecast.border)};
  border-radius: 0.25rem;
  color: ${({ theme, $warn }) =>
    $warn ? '#f97316' : theme.forecast.secondaryText};
`

const Empty = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export interface SentinelInstancePickerProps {
  instances: AssetInstanceSummary[]
  isLoading: boolean
  selectedSlug: string
  onSelect: (slug: string) => void
}

/**
 * Picks the filled asset instance the whole flow is driven from.
 *
 * The row shows fill status and slot coverage because they decide whether an
 * instance is worth modelling at all — a `control_valve.v1` with 1 of 4
 * required PI-tag slots bound is a common shape, and it is better to see that
 * here than to discover it from a failed context build.
 */
export const SentinelInstancePicker: FC<SentinelInstancePickerProps> = ({
  instances,
  isLoading,
  selectedSlug,
  onSelect,
}) => {
  const { t } = useLingui()

  if (isLoading && instances.length === 0) {
    return (
      <Empty>
        <Trans>Loading asset instances…</Trans>
      </Empty>
    )
  }
  if (instances.length === 0) {
    return (
      <Empty>
        <Trans>
          No filled asset instance on this site yet. Fill one from the Assets
          tab first — a sentinel is designed from the fill&apos;s bound tags.
        </Trans>
      </Empty>
    )
  }

  return (
    <List role='listbox' aria-label={t`Asset instances`}>
      {instances.map((instance) => {
        const noProcessData = !instance.hasProcessData
        return (
          <Card
            key={instance.slug}
            type='button'
            role='option'
            aria-selected={instance.slug === selectedSlug}
            $selected={instance.slug === selectedSlug}
            onClick={() => onSelect(instance.slug)}>
            <CardName>{instance.name}</CardName>
            <Meta>
              {instance.assetType && <Chip>{instance.assetType}</Chip>}
              {instance.system && <Chip>{instance.system}</Chip>}
              <Chip $warn={instance.state === 'failed'}>
                {fillStatusLabel(instance.state)}
              </Chip>
              <Chip $warn={instance.gapCount > 0}>
                {instance.filledCount}/{instance.totalCount}
                <Trans> slots bound</Trans>
              </Chip>
              <Chip $warn={noProcessData}>
                {noProcessData ? t`no PI tags` : t`PI tags bound`}
              </Chip>
              <Chip $warn={!instance.hasDocuments}>
                {instance.hasDocuments ? t`documents bound` : t`no documents`}
              </Chip>
            </Meta>
          </Card>
        )
      })}
    </List>
  )
}
